// ============================================================================
// <FacilityMap> — the shared map primitive (WS8).
//
// READ-ONLY AND ROUTE-AGNOSTIC by design: this component must not know routes
// exist (My Route consumes it later with a different scope; if route state
// leaks in here it has to be extracted again — the rebuild risk Ben cares
// about). Three consumers, one component, a scope prop:
//   Accounts Map tab  -> facilityIds = current list filter, fit-to-bounds
//   Home "Go to" tile -> all facilities, centre on user
//   My Route (later)  -> one AM's stops, fit-to-route
//
// Google Maps JS SDK via @react-google-maps/api. The browser key is fetched
// at runtime by signed-in users (vault -> get_maps_browser_key RPC) and is
// NEVER bundled. While the key is unset the component renders a graceful
// unavailable state — pins and data logic stay testable.
//
// Data: v_map_facilities / v_map_offices only (WS7 contract). Never join
// service_assignments here (FAC-0162 fan-out).
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GoogleMap,
  MarkerClustererF,
  MarkerF,
  PolylineF,
  useJsApiLoader,
} from '@react-google-maps/api';
import { MapPin, Maximize2, Minimize2, Route } from 'lucide-react';
import type { Clusterer } from '@react-google-maps/marker-clusterer';
import { token } from '../design-tokens';
import { useTheme } from '../theme';
import type {
  FacilityMapScope,
  MapDimension,
  MapFacility,
  MapOffice,
} from './map-types';
import {
  bucketOf,
  closedColor,
  mutedColor,
  pinRingColor,
  OFFICE_COLOR,
} from './map-colors';
import { useMapData, useMapsBrowserKey } from './use-map-data';
import { MapFilterBar, useMapFilters } from './map-filters';
import { FacilitySheet } from './facility-sheet';
import { useRoutePlanner, type PlannerStop } from './use-route-planner';
import { RoutePanel } from './route-panel';
import { isCoordTrusted } from './navigate-link';

/**
 * MapFacility → PlannerStop. Module scope on purpose: the pin-tap handler and
 * Add-All both need it and they had already drifted apart once.
 *
 * `address` and `coordTrusted` feed the SINGLE-destination next-stop handoff
 * only (route-panel's nextLegUrl). Multi-stop route MATH reads lat/lng and
 * nothing else — that is the hard rule.
 */
const toPlannerStop = (f: MapFacility): PlannerStop => ({
  id: f.facility_id,
  lat: f.latitude,
  lng: f.longitude,
  name: f.facility_name,
  address: f.facility_address,
  coordTrusted: isCoordTrusted(f.geocode_precision, f.coord_verified_result),
});

// KC metro default centre (prior spec, kept).
const DEFAULT_CENTER = { lat: 39.0997, lng: -94.5786 };

const CONTAINER_STYLE: React.CSSProperties = { width: '100%', height: '100%' };

// star (offices) / diamond (dock & storage) SVG paths, 24-box
const STAR_PATH =
  'M12 2l2.9 6.2 6.8.9-5 4.7 1.3 6.7L12 17.3 6 20.5l1.3-6.7-5-4.7 6.8-.9z';
const DIAMOND_PATH = 'M12 2l9 10-9 10-9-10z';

function normAddr(a: string | null | undefined): string {
  // Dedupe key for the office-vs-facility layer collision (G6). Suite/unit
  // tokens are stripped because the SAME building legitimately differs on
  // them (OF-001 '400 E Red Bridge Rd' vs FAC-0241 '… Suite 312').
  return (a ?? '')
    .toLowerCase()
    .replace(/[.,#]/g, ' ')
    .replace(/\b(suite|ste|unit|bldg|building)\s*[\w-]+\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * How the map sizes itself.
 *
 *   'flex'  (default) — fill the available content area. The consumer owns the
 *                       height; use dvh units, never vh.
 *   'fixed'           — the legacy 32rem box, for embedded card contexts
 *                       (facility detail, dashboard tiles) that must NOT grow
 *                       to full height.
 */
export type MapHeight = 'flex' | 'fixed';

export interface FacilityMapProps extends FacilityMapScope {
  /** viewport height of the map area; consumers size their own container */
  className?: string;
  dimension?: MapDimension;
  /** see MapHeight. Defaults to 'flex' as of CW-I Phase 3. */
  height?: MapHeight;
  /**
   * Allow the fullscreen toggle. Off for embedded cards, where a fullscreen
   * button in a small tile is noise.
   */
  allowFullscreen?: boolean;
}

export function FacilityMap({
  facilityIds,
  initialCenter = 'fit',
  className,
  dimension: initialDimension = 'type',
  height = 'flex',
  allowFullscreen = true,
}: FacilityMapProps) {
  // CW-I Phase 3b. Fullscreen is LOCAL state on this component, deliberately:
  // filters (useMapFilters), the selected pin, and the route planner all live
  // below this line, so entering and leaving fullscreen re-parents the DOM but
  // never remounts them. That is what preserves filter state and the map's own
  // centre/zoom across the transition — losing someone's pan after they escape
  // a fullscreen map is worse than the small map ever was.
  const [fullscreen, setFullscreen] = useState(false);

  // Escape exits. It is a convenience, NOT the exit affordance — the visible
  // button below is, because a phone has no Escape key.
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    document.addEventListener('keydown', onKey);
    // App chrome (sidebar, header, mobile bottom nav) is hidden by taking the
    // map out of flow at z-50; locking body scroll stops the page behind it
    // from moving under the fixed layer.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullscreen]);
  const { facilities, offices, loading, error } = useMapData();
  const { key, keyLoading } = useMapsBrowserKey();

  // scope restriction (e.g. Accounts list current filter)
  const scoped = useMemo(() => {
    if (!facilityIds) return facilities;
    const set = new Set(facilityIds);
    return facilities.filter((f) => set.has(f.facility_id));
  }, [facilities, facilityIds]);

  // WS9: dimension switcher + legend own the colour state.
  const filters = useMapFilters(scoped, initialDimension);
  const { visible, dimension, palette, mutedBuckets, selectedSubs, showOffices } =
    filters;

  // 2026-07-31 (Ben's field feedback): legend mute is a real FILTER — muted
  // buckets are REMOVED from the map so cluster counts stay truthful. In sub
  // mode with selections, unselected subs stay visible as grey CONTEXT but
  // are rendered OUTSIDE the clusterer, so clusters only count the subs
  // you're comparing.
  const activeFacilities = useMemo(() => {
    if (dimension === 'sub') {
      if (selectedSubs.size === 0) return visible;
      return visible.filter((f) => selectedSubs.has(bucketOf(f, 'sub')));
    }
    if (mutedBuckets.size === 0) return visible;
    return visible.filter((f) => !mutedBuckets.has(bucketOf(f, dimension)));
  }, [visible, dimension, selectedSubs, mutedBuckets]);

  const contextFacilities = useMemo(
    () =>
      dimension === 'sub' && selectedSubs.size > 0
        ? visible.filter((f) => !selectedSubs.has(bucketOf(f, 'sub')))
        : [],
    [visible, dimension, selectedSubs],
  );

  const [selected, setSelected] = useState<MapFacility | null>(null);

  // Route Optimizer (2026-07-31, D-4): a MODE on this shared component, so
  // /map and the Accounts Map tab get it automatically. All route state
  // lives in the hook + panel; LoadedMap below stays route-agnostic and only
  // receives presentational props (highlight set, numbered path).
  const planner = useRoutePlanner();
  const planning = planner.state.active;

  const plannerSelectedIds = useMemo(
    () => new Set(planner.state.stops.map((s) => s.id)),
    [planner.state.stops],
  );

  const routeMarkers = useMemo(() => {
    if (!planner.state.orderedIds) return [];
    const byId = new Map(planner.state.stops.map((s) => [s.id, s]));
    return planner.state.orderedIds.flatMap((id, i) => {
      const s = byId.get(id);
      return s ? [{ id, lat: s.lat, lng: s.lng, label: String(i + 1) }] : [];
    });
  }, [planner.state.orderedIds, planner.state.stops]);

  const routePath = useMemo(() => {
    const pts = routeMarkers.map((m) => ({ lat: m.lat, lng: m.lng }));
    if (planner.state.start && pts.length > 0) {
      return [planner.state.start, ...pts];
    }
    return pts;
  }, [routeMarkers, planner.state.start]);

  const handlePinTap = useCallback(
    (f: MapFacility) => {
      if (planner.state.active) {
        // selection mode: tap toggles the stop, never opens the sheet.
        // Uses the SAME mapper as Add-All — these two had drifted apart once
        // already (Phase 1 added `address` here by hand), so there is now
        // exactly one definition of MapFacility → PlannerStop.
        planner.toggleStop(toPlannerStop(f));
        return;
      }
      setSelected(f);
    },
    [planner],
  );

  // offices dedupe against the facility layer by address (OF-001/FAC-0241,
  // OF-008/FAC-0175 are the same buildings) — display concern, fixed in the
  // map, never in the data.
  const visibleOffices = useMemo(() => {
    if (!showOffices) return [] as MapOffice[];
    const facAddrs = new Set(visible.map((f) => normAddr(f.facility_address)));
    return offices.filter(
      (o) =>
        o.latitude != null &&
        o.longitude != null &&
        !facAddrs.has(normAddr(o.address)),
    );
  }, [offices, showOffices, visible]);

  if (keyLoading || loading) {
    return (
      <Shell className={className} height={height} fullscreen={fullscreen} onExitFullscreen={() => setFullscreen(false)}>
        <div className="flex h-full items-center justify-center text-sm text-tcs-text-muted">
          Loading map…
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell className={className} height={height} fullscreen={fullscreen} onExitFullscreen={() => setFullscreen(false)}>
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-tcs-text-muted">
          Couldn&apos;t load map data: {error}
        </div>
      </Shell>
    );
  }

  if (!key) {
    // Vault secret maps_browser_key not populated yet — graceful, honest.
    return (
      <Shell className={className} height={height} fullscreen={fullscreen} onExitFullscreen={() => setFullscreen(false)}>
        <MapFilterBar filters={filters} />
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
          <MapPin className="h-6 w-6 text-tcs-text-muted" aria-hidden />
          <div className="text-sm font-medium">Map tiles unavailable</div>
          <div className="max-w-sm text-xs text-tcs-text-muted">
            The Google Maps key isn&apos;t configured yet ({visible.length}{' '}
            facilities ready to render). The dimension chips and legend above
            are live; tiles appear as soon as the key lands — no redeploy
            needed.
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell className={className} height={height} fullscreen={fullscreen} onExitFullscreen={() => setFullscreen(false)}>
      <MapFilterBar
        filters={filters}
        compact={fullscreen}
        trailing={
          <>
          {allowFullscreen && !fullscreen && (
            <button
              type="button"
              onClick={() => setFullscreen(true)}
              aria-label="Open map fullscreen"
              className="flex h-11 shrink-0 items-center gap-1.5 rounded-lg border border-tcs-border bg-tcs-raised px-3 text-[11px] font-semibold text-tcs-text"
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden />
              Fullscreen
            </button>
          )}
          <button
            type="button"
            aria-pressed={planning}
            onClick={() => {
              if (planning) {
                planner.exit();
              } else {
                setSelected(null); // sheet closes; taps now select stops
                planner.enter();
              }
            }}
            className={
              // h-11 = the 44px touch floor (Gate 4) — deliberately taller
              // than the sibling filter toggles; it's the mode switch.
              'flex h-11 shrink-0 items-center gap-1.5 rounded-lg border px-3 text-[11px] font-semibold transition-colors ' +
              (planning
                ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                : 'border-tcs-border bg-tcs-raised text-tcs-text')
            }
          >
            <Route className="h-3.5 w-3.5" aria-hidden />
            {planning ? 'Exit planning' : 'Plan route'}
            {planning && (
              <span className="rounded-full bg-tcs-inverse-ink/20 px-1.5 font-mono text-[10px]">
                {planner.state.stops.length}
              </span>
            )}
          </button>
          </>
        }
      />
      <div className="relative min-h-0 flex-1">
        <LoadedMap
          apiKey={key}
          facilities={activeFacilities}
          contextFacilities={contextFacilities}
          offices={visibleOffices}
          dimension={dimension}
          palette={palette}
          mutedBuckets={mutedBuckets}
          selectedSubs={selectedSubs}
          initialCenter={initialCenter}
          selectedId={selected?.facility_id ?? null}
          onSelect={handlePinTap}
          highlightIds={planning ? plannerSelectedIds : undefined}
          routeMarkers={planning ? routeMarkers : undefined}
          routePath={planning ? routePath : undefined}
        />
        {!planning && (
          <FacilitySheet facility={selected} onClose={() => setSelected(null)} />
        )}
        <RoutePanel
          planner={planner}
          addAllCount={activeFacilities.length}
          onAddAll={() => planner.addAll(activeFacilities.map(toPlannerStop))}
        />
      </div>
    </Shell>
  );
}

// ============================================================================
// Shell — CW-I Phase 3a. Was `h-[32rem]`, unconditionally, regardless of
// viewport. That 512px pin IS the desktop dead-space complaint, and it
// contradicted this component's own documented contract two hundred lines up:
// "viewport height of the map area; consumers size their own container".
//
// Worth knowing WHY passing a className never fixed it: FacilityMapPage has
// been passing `h-[calc(100vh-11rem)]` since WS12 and the map stayed 512px.
// Both `h-[32rem]` and the consumer's height landed in the same class list
// with equal specificity, so the winner was whichever Tailwind emitted later
// in the stylesheet — not whichever appeared later in the attribute. A height
// prop cannot be overridden by concatenation; it has to not be there.
//
// Default is now `flex` — fill the container, let the consumer own the height.
// `fixed` restores the old 32rem for genuinely embedded card contexts
// (facility detail, dashboard tiles) so those never grow to full height.
// There are no such consumers today; the prop exists so the next one does not
// have to re-derive this.
//
// dvh, not vh: on mobile Safari/Chrome `vh` measures the viewport WITHOUT
// browser chrome, so a vh-sized map has its bottom clipped behind the URL bar.
function Shell({
  className,
  height = 'flex',
  fullscreen = false,
  onExitFullscreen,
  children,
}: {
  className?: string;
  height?: MapHeight;
  fullscreen?: boolean;
  onExitFullscreen?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={
        fullscreen
          ? // Full-bleed, above app chrome (sidebar z-30, mobile bottom nav
            // z-40). dvh so the bottom is not clipped behind mobile browser
            // chrome — the exact bug `vh` causes.
            'fixed inset-0 z-50 flex h-[100dvh] w-screen flex-col overflow-hidden bg-tcs-bg'
          : 'relative flex min-h-0 flex-col overflow-hidden rounded-xl border border-tcs-border bg-tcs-bg ' +
            (height === 'fixed' ? 'h-[32rem] ' : 'h-full flex-1 ') +
            (className ?? '')
      }
    >
      {children}
      {fullscreen && (
        // ALWAYS VISIBLE exit. Not a swipe gesture, not browser back —
        // someone will hit this on a phone in a hallway at 10pm and needs an
        // obvious way out. 44px floor, and it sits above the map layer.
        // pb-[env(safe-area-inset-bottom)] keeps it clear of the iOS home bar.
        <button
          type="button"
          onClick={onExitFullscreen}
          className="absolute bottom-4 left-1/2 z-[60] inline-flex h-11 -translate-x-1/2 items-center gap-2 rounded-full border border-tcs-border bg-tcs-bg px-5 text-sm font-semibold text-tcs-text shadow-lg"
          style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
        >
          <Minimize2 className="h-4 w-4" aria-hidden />
          Exit fullscreen
        </button>
      )}
    </div>
  );
}

interface LoadedMapProps {
  apiKey: string;
  facilities: MapFacility[];
  offices: MapOffice[];
  dimension: MapDimension;
  palette: Map<string, string>;
  mutedBuckets: Set<string>;
  selectedSubs: Set<string>;
  initialCenter: 'fit' | 'user';
  selectedId: string | null;
  onSelect: (f: MapFacility) => void;
  // Presentational route-planning props (2026-07-31). LoadedMap has NO route
  // semantics — it just highlights these ids and draws this numbered path.
  highlightIds?: Set<string>;
  routeMarkers?: { id: string; lat: number; lng: number; label: string }[];
  routePath?: google.maps.LatLngLiteral[];
  /** grey context pins rendered OUTSIDE the clusterer (sub mode) so cluster
   *  counts only reflect the active set — Ben's field feedback 2026-07-31 */
  contextFacilities?: MapFacility[];
}

function LoadedMap({
  apiKey,
  facilities,
  contextFacilities,
  offices,
  dimension,
  palette,
  mutedBuckets,
  selectedSubs,
  initialCenter,
  selectedId,
  onSelect,
  highlightIds,
  routeMarkers,
  routePath,
}: LoadedMapProps) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'tcs-google-maps',
    googleMapsApiKey: apiKey,
  });
  const mapRef = useRef<google.maps.Map | null>(null);

  // ==========================================================================
  // CW-J Phase 2 — dark map tiles (Ben's decision 5).
  //
  // ⚠️ `colorScheme` is INIT-ONLY. Google reads it when the map is constructed
  // and ignores every later change, so there is no setter to call on a theme
  // switch — the map has to be rebuilt. We force that with a key.
  //
  // Rebuilding naively would drop the AM wherever fitBounds puts them, which
  // on My Route means losing their place mid-route. So we snapshot the live
  // centre and zoom on unmount and restore it on the next onLoad instead of
  // re-fitting. First mount has no snapshot and fits bounds as before.
  // ==========================================================================
  const { resolved } = useTheme();
  const restoreRef = useRef<{ center: google.maps.LatLngLiteral; zoom: number } | null>(null);

  useEffect(() => {
    // Runs on unmount of each keyed instance, i.e. exactly when the theme
    // changed and React is about to build a fresh map.
    return () => {
      const map = mapRef.current;
      if (!map) return;
      const c = map.getCenter();
      const z = map.getZoom();
      if (c && typeof z === 'number') {
        restoreRef.current = { center: { lat: c.lat(), lng: c.lng() }, zoom: z };
      }
      mapRef.current = null;
    };
  }, [resolved]);
  const [userPos, setUserPos] = useState<google.maps.LatLngLiteral | null>(null);

  useEffect(() => {
    if (initialCenter !== 'user' || !('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setUserPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setUserPos(null),
      { maximumAge: 300_000, timeout: 8_000 },
    );
  }, [initialCenter]);

  const fitBounds = useCallback(
    (map: google.maps.Map) => {
      if (initialCenter === 'user' && userPos) {
        map.setCenter(userPos);
        map.setZoom(11);
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      let n = 0;
      for (const f of facilities) {
        bounds.extend({ lat: f.latitude, lng: f.longitude });
        n += 1;
      }
      if (n === 0) {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(9);
      } else if (n === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(13);
      } else {
        map.fitBounds(bounds, 48);
      }
    },
    [facilities, initialCenter, userPos],
  );

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      const restore = restoreRef.current;
      if (restore) {
        // A theme switch, not a first mount: put the AM back exactly where
        // they were rather than re-fitting to all pins.
        restoreRef.current = null;
        map.setCenter(restore.center);
        map.setZoom(restore.zoom);
        return;
      }
      fitBounds(map);
    },
    [fitBounds],
  );

  // Resolved ONCE per render. `colorFor` returns this exact string for a muted
  // pin and :582/:588 compare by identity to decide opacity and z-index — see
  // the note in design-tokens.ts. Recomputing inside the callback would still
  // be equal by value, but pinning it here makes the dependency explicit.
  const muted = mutedColor();
  const pinStroke = token('--tcs-raised');

  const colorFor = useCallback(
    (f: MapFacility): string => {
      if (f.status === 'Closed') return closedColor();
      if (dimension === 'sub') {
        // select-and-highlight only — NEVER one colour per sub (70 active)
        const b = bucketOf(f, 'sub');
        if (selectedSubs.size === 0) return muted;
        return selectedSubs.has(b) ? palette.get(b) ?? muted : muted;
      }
      const b = bucketOf(f, dimension);
      if (mutedBuckets.has(b)) return muted;
      return palette.get(b) ?? muted;
    },
    [dimension, muted, mutedBuckets, palette, selectedSubs],
  );

  if (loadError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-tcs-text-muted">
        Google Maps failed to load — check the browser key referrer settings.
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-tcs-text-muted">
        Loading Google Maps…
      </div>
    );
  }

  return (
    <GoogleMap
      // Remount on a theme change — colorScheme cannot be set after construction.
      key={`map-${resolved}`}
      mapContainerStyle={CONTAINER_STYLE}
      onLoad={onLoad}
      options={{
        clickableIcons: false,
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        // Typed as a string enum in newer @types/google.maps; cast keeps this
        // working against the version pinned here without widening options.
        colorScheme: (resolved === 'dark' ? 'DARK' : 'LIGHT') as never,
      }}
    >
      {/* key forces the clusterer to rebuild when the ACTIVE set changes —
          without it, stale clusters can keep counting hidden pins */}
      <MarkerClustererF
        key={`${dimension}:${facilities.length}:${mutedBuckets.size}:${selectedSubs.size}`}
        averageCenter
        maxZoom={14}
        gridSize={44}
      >
        {(clusterer: Clusterer) => (
          <>
            {facilities.map((f) => {
              const color = colorFor(f);
              const isSel = f.facility_id === selectedId;
              const isHighlighted = highlightIds?.has(f.facility_id) ?? false;
              return (
                <MarkerF
                  key={f.facility_id}
                  position={{ lat: f.latitude, lng: f.longitude }}
                  clusterer={clusterer}
                  title={f.facility_name}
                  onClick={() => onSelect(f)}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: color,
                    fillOpacity: color === muted ? 0.45 : 1,
                    strokeColor: isHighlighted ? pinRingColor() : pinStroke,
                    strokeWeight: isHighlighted ? 3 : 2,
                    scale: isHighlighted ? 10 : isSel ? 10 : 7,
                  }}
                  zIndex={
                    isHighlighted ? 35 : isSel ? 30 : color === muted ? 1 : 10
                  }
                />
              );
            })}
          </>
        )}
      </MarkerClustererF>

      {/* sub-mode context layer: unselected subs, grey, OUTSIDE the
          clusterer — clusters above only count the subs being compared */}
      {contextFacilities?.map((f) => (
        <MarkerF
          key={`ctx-${f.facility_id}`}
          position={{ lat: f.latitude, lng: f.longitude }}
          title={f.facility_name}
          onClick={() => onSelect(f)}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: muted,
            fillOpacity: 0.4,
            strokeColor: pinStroke,
            strokeWeight: 1.5,
            scale: 5.5,
          }}
          zIndex={1}
        />
      ))}

      {offices.map((o) => (
        <MarkerF
          key={o.office_id}
          position={{ lat: o.latitude as number, lng: o.longitude as number }}
          title={`${o.office_name} (TCS ${o.site_kind})`}
          icon={{
            path: o.site_kind === 'office' ? STAR_PATH : DIAMOND_PATH,
            fillColor: OFFICE_COLOR,
            fillOpacity: 1,
            strokeColor: pinStroke,
            strokeWeight: 1.5,
            scale: 0.9,
            anchor: new google.maps.Point(12, 12),
          }}
          zIndex={20}
        />
      ))}

      {/* presentational route overlay — numbered path handed down as props */}
      {routePath && routePath.length > 1 && (
        <PolylineF
          path={routePath}
          options={{
            strokeColor: '#3B49C4',
            strokeOpacity: 0.85,
            strokeWeight: 3,
            clickable: false,
          }}
        />
      )}
      {routeMarkers?.map((m) => (
        <MarkerF
          key={`route-${m.id}`}
          position={{ lat: m.lat, lng: m.lng }}
          title={`Stop ${m.label}`}
          label={{
            text: m.label,
            color: pinStroke,
            fontSize: '11px',
            fontWeight: '700',
          }}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: '#3B49C4',
            fillOpacity: 1,
            strokeColor: pinStroke,
            strokeWeight: 2,
            scale: 11,
          }}
          zIndex={45}
          onClick={() => {
            const f = facilities.find((x) => x.facility_id === m.id);
            if (f) onSelect(f);
          }}
        />
      ))}

      {userPos && (
        <MarkerF
          position={userPos}
          title="You are here"
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: '#3B49C4',
            fillOpacity: 1,
            strokeColor: pinStroke,
            strokeWeight: 3,
            scale: 8,
          }}
          zIndex={40}
        />
      )}
    </GoogleMap>
  );
}
